import { zip } from 'lodash';
import keyBy from 'lodash/keyBy';
import Notifications from 'react-notification-system-redux';
import { APIScenarioReturn } from '../../../general/apiSagaFactory';
import { CopyMoveProps, api } from '../../../general/apiScenarios';
import { CHNode, NodeLabel } from '../../../general/node';
import { sequentialAsyncMap } from '../../../general/sagas/seq';
import { Interrupt, TSagaReturn, TSagaReturnSuccess } from '../../../general/scenarioFactory';
import { scenarioFactoryAsync } from '../../../general/scenarioFactoryAsync';
import { ListenerApiLight, _select, _take, lm } from '../../listenerMiddleware';
import { logger } from '../../logger';
import { Page } from '../../models/page';
import { getNodeId, getPage } from '../app/selectors';
import { apiRequestBreadcrumbs } from '../breadcrumbs/listeners';
import * as conflictResolverActions from '../conflictResolver/actions';
import { AutoConflictResolution, OverwriteSetting } from '../conflictResolver/model';
import { resolve } from '../conflictResolver/sagas';

import * as routerActions from '../../../general/modules/router/actions';

import * as fileManagerActions from '../fileManager/actions';
import { importWorkflow } from '../creativeWorkflow';
import { getSelectedNodesInAllFolders } from '../fileManager/selectors/selectors';
import * as moverActions from './actions';
import { Messages, MoverDialogMode, TRichResult } from './model';
import {
  OriginTargetCombination,
  createBreadcrumbsMemoized,
  createFullBreadcrumbs,
  getCurrentCombination,
  getMoverDialog,
  getSelectedNode,
  getTree,
} from './selectors';

export function moverListener() {
  watchTriggerButtonClicked();
}

export function watchTriggerButtonClicked() {
  lm.takeEvery(moverActions.triggerButtonClicked, async function (action, lapi) {
    const page = _select(lapi, getPage);
    let sourceNodeID: number | null = null;

    if (page === Page.SEARCH) {
      const result = await apiRequestBreadcrumbs(undefined, page, null);
      if (result.success) {
        sourceNodeID = result.data.response.nodes[0].id;
      }
    } else {
      sourceNodeID = _select(lapi, getNodeId);
    }
    if (sourceNodeID === null) return;
    const selected = _select(lapi, getSelectedNodesInAllFolders);
    await scenario(lapi, sourceNodeID, action.payload.mode, selected);
  });
}

async function checkConfirm(lapi: ListenerApiLight) {
  const { type } = await _take(
    lapi,
    moverActions.modalCloseUiEvent,
    moverActions.cancelButtonClicked,
    moverActions.confirmButtonClicked,
  );
  if (type !== moverActions.confirmButtonClicked.type) throw Interrupt.UserCancelled;
  return true;
}

export async function processNode(
  lapi: ListenerApiLight,
  node: NodeLabel,
  target: Pick<CHNode, 'id'>,
  mode: MoverDialogMode.COPY | MoverDialogMode.MOVE,
  overwrite: OverwriteSetting,
  new_name?: string,
): Promise<APIScenarioReturn<{ id: number } | 'skipped'>> {
  const copyMoveProps: CopyMoveProps = {
    new_parent_node_id: target.id,
    node_id: node.id,
    overwrite: overwrite === OverwriteSetting.Overwrite,
    new_name: new_name,
  };
  let result;

  if (mode === MoverDialogMode.COPY) {
    result = await api.fs.copyNodeAsync(copyMoveProps);
    if (result.success) {
      logger.lapi(lapi, {
        action: 'File',
        sub_action: 'Copy',
        extra: { int_1: node.id, int_3: target.id },
      });
      return result;
    }
  } else {
    result = await api.fs.moveNode(copyMoveProps);
    if (result.success) {
      logger.lapi(lapi, {
        action: 'File',
        sub_action: 'Move',
        extra: { int_1: node.id, int_3: target.id },
      });
      return {
        ...result,
        data: { ...result.data, response: { id: result.data.response.node.id } },
      };
    }
  }
  const status = result.errorData?.status!;
  if (status !== 400) {
    // ok
    return result;
  }
  if (result.errorData?.response?.code === 'invalid') {
    return result;
  }
  //fail
  if (overwrite === OverwriteSetting.Overwrite || overwrite === OverwriteSetting.NoOverwrite) {
    return result;
  }

  const autoResolution = await resolve(lapi, node.name);
  if (autoResolution === AutoConflictResolution.Overwrite) {
    return await processNode(lapi, node, target, mode, OverwriteSetting.Overwrite);
  }
  // был скип
  return {
    args: result.args,
    success: true,
    data: { response: 'skipped' },
  } as TSagaReturnSuccess;
}

async function processNodes(
  lapi: ListenerApiLight,
  nodes: CHNode[],
  target: CHNode,
  mode: MoverDialogMode.COPY | MoverDialogMode.MOVE,
): Promise<TSagaReturn<TRichResult[]>> {
  const currentNodeID = _select(lapi, getNodeId);
  const nodesByID = keyBy(nodes, (node) => node.id);

  // делаем дела
  lapi.dispatch(moverActions.setInprogress({ inProgress: true }));
  const callbacks = nodes.map(
    (node) => async () => await processNode(lapi, node, target, mode, OverwriteSetting.Ask),
  );
  const results = await sequentialAsyncMap(callbacks);
  const nodesAndResults = zip(nodes, results).filter(
    (x): x is [CHNode, APIScenarioReturn<'skipped' | { id: number }>] =>
      x[0] !== undefined && x[1] !== undefined,
  );
  lapi.dispatch(moverActions.setInprogress({ inProgress: false }));
  lapi.dispatch(conflictResolverActions.contextReset());

  for (const nodeAndResult of nodesAndResults) {
    const [node, result] = nodeAndResult;
    if (result.success && result.data.response !== 'skipped') {
      if (target.id === currentNodeID) {
        lapi.dispatch(fileManagerActions.nodeAdded({ node: { ...node, ...result.data.response } }));
      }
      if (mode === MoverDialogMode.MOVE) {
        lapi.dispatch(fileManagerActions.nodeRemoved({ node }));
      }
    }
    if (!result.success) {
      const nfe = Array.isArray(result.errorData?.response?.data?.non_field_errors)
        ? result.errorData.response.data.non_field_errors
        : Array.isArray(result.errorData?.response?.data)
          ? result.errorData.response.data
          : undefined;

      let message = nfe?.join(', ') ?? JSON.stringify((result.reason as any)?.response);

      // Add server response message if available
      const serverMessage = result.errorData?.response?.data?.message;
      if (serverMessage) {
        message = serverMessage;
      }

      lapi.dispatch(
        Notifications.error({
          title: mode === MoverDialogMode.COPY ? `Failed to copy file` : `Failed to move file`,
          message,
        }),
      );
    }
  }

  // собираем результаты
  const richResults = nodesAndResults.map(([, result]): TRichResult => {
    return {
      ...result,
      node: nodesByID[result.args[0]],
    };
  });

  const failedJobs = richResults.filter((result) => !result.success);
  if (failedJobs.length > 0) {
    const failedNodeNames = failedJobs.map((failedJob) => failedJob.node.name);
    lapi.dispatch(Notifications.error(Messages.error(failedNodeNames, mode)));
  } else {
    lapi.dispatch(Notifications.success(Messages.success(richResults, mode)));
  }
  return {
    success: true,
    args: { nodes, selectedNode: target },
    data: richResults,
  } as TSagaReturn;
}

async function scenarioInner(
  lapi: ListenerApiLight,
  originNodeId: number,
  mode: MoverDialogMode,
  nodes: CHNode[],
) {
  const dialog = _select(lapi, getMoverDialog);
  if (dialog.isOpen) {
    // он уже открыт? почему?
    throw new Error('opened already');
  }

  lapi.dispatch(moverActions.dialogOpened({ mode, sourceNodeID: originNodeId }));

  // true = нажал ОК, false = отмена или диммер
  await checkConfirm(lapi);

  const selectedNode = _select(lapi, getSelectedNode);
  if (selectedNode === null) {
    // если ничего не выбрано (такое возможно?), просто уходим
    throw new Error('no node selected');
  }

  // сценарий был запущен откуда-то еще, мы не должны делать ничего с нодами
  if (mode === MoverDialogMode.EXTERNAL) {
    const tree = _select(lapi, getTree);
    const breadcrumbs = createFullBreadcrumbs(selectedNode.id, tree.contents);
    return breadcrumbs;
  }

  const combo = _select(lapi, getCurrentCombination);
  if (combo === OriginTargetCombination.IMPORT) {
    // был выбран креатив
    // вызываем новый флоу импорта
    await importWorkflow(
      lapi,
      nodes,
      selectedNode.id,
      mode === MoverDialogMode.COPY ? 'COPY' : 'MOVE',
    );
    return selectedNode.id;
  } else {
    const result = await processNodes(lapi, nodes, selectedNode, mode);
    if (result.success) {
      lapi.dispatch(fileManagerActions.allDeselected({}));
      return result.data;
    }
    throw result.reason;
  }
}

export const scenario = scenarioFactoryAsync(
  scenarioInner,
  async (lapi) => {
    await _take(lapi, routerActions.pagePathChanged);
  },
  (lapi: ListenerApiLight) => {
    lapi.dispatch(moverActions.dialogClosed());
    const cache = createBreadcrumbsMemoized.cache;
    if (cache.clear) {
      cache.clear();
    }

    return true;
  },
  'Mover',
);
